import { onActivated, onBeforeUnmount, onDeactivated, onMounted } from 'vue';

// Call in setup and register the viewTracking Vuex module in the consuming app.
// v-track-view="{ event, properties, key, threshold: 0.5, duration: 1000 }"
// Uses the Vuex session by default; sessionId can override it.
export default function useTrackView(store) {
  const trackers = new Map();
  let active = true;

  const cancelTimer = (tracker) => {
    clearTimeout(tracker.timer);
    tracker.timer = undefined;
  };
  const stop = (tracker) => {
    cancelTimer(tracker);
    tracker.observer.disconnect();
  };
  const observe = (tracker, el) => {
    if (active && !tracker.viewed && !document.hidden) {tracker.observer.observe(el);}
  };
  const mountTracker = (el, { value }) => {
    if (!window.IntersectionObserver) {return;}
    const threshold = value.threshold ?? 0.5;
    const tracker = { value, viewed: false };

    tracker.observer = new IntersectionObserver((entries) => {
      if (!active || tracker.viewed || document.hidden || trackers.get(el) !== tracker) {return;}
      entries.forEach((entry) => {
        if (!entry.isIntersecting || entry.intersectionRatio < threshold) {
          cancelTimer(tracker);
        } else if (tracker.timer === undefined) {
          tracker.timer = setTimeout(() => {
            stop(tracker);
            if (!active || document.hidden) {return;}
            tracker.viewed = true;
            store.dispatch('viewTracking/trackView', tracker.value);
          }, value.duration ?? 1000);
        }
      });
    }, { threshold: [0, threshold] });
    trackers.set(el, tracker);
    observe(tracker, el);
  };
  const unmountTracker = (el) => {
    const tracker = trackers.get(el);

    if (tracker) {stop(tracker);}
    trackers.delete(el);
  };
  const handleVisibilityChange = () => {
    trackers.forEach((tracker, el) => {
      if (document.hidden) {stop(tracker);} else {observe(tracker, el);}
    });
  };
  const identity = (value) => JSON.stringify([value.sessionId, value.event, value.key, value.threshold, value.duration]);

  onMounted(() => document.addEventListener('visibilitychange', handleVisibilityChange));
  onActivated(() => {
    active = true;
    trackers.forEach(observe);
  });
  onDeactivated(() => {
    active = false;
    trackers.forEach(stop);
  });
  onBeforeUnmount(() => {
    active = false;
    trackers.forEach(stop);
    trackers.clear();
    document.removeEventListener('visibilitychange', handleVisibilityChange);
  });

  return {
    mounted: mountTracker,
    updated(el, binding) {
      const tracker = trackers.get(el);

      if (!tracker) {return;}
      if (identity(binding.value) !== identity(binding.oldValue)) {
        unmountTracker(el);
        mountTracker(el, binding);
      } else {
        tracker.value = binding.value;
      }
    },
    beforeUnmount: unmountTracker,
  };
}
